/**
 * Soft beta-readiness gate for Publish wizard.
 * Export remains allowed; UI surfaces blockers/warnings and asks for acknowledgment.
 */

import { chapterContentToPlainText } from "./chapterContent";
import type { Book } from "./models";

export type PublishReadinessStoryCraftChapter = {
  checklist?: { suggestion: string }[];
  summary?: string;
  average?: number | null;
};

export type PublishReadinessOptions = {
  storyCraftByChapter?: Record<string, PublishReadinessStoryCraftChapter>;
};

export type PublishReadinessResult = {
  ok: boolean;
  blockers: string[];
  warnings: string[];
};

function chapterHasContent(content: unknown): boolean {
  return chapterContentToPlainText(content).trim().length > 0;
}

function isSpineChapter(kind: string | undefined): boolean {
  return !kind || kind === "chapter";
}

export function assessPublishReadiness(
  book: Book,
  opts?: PublishReadinessOptions
): PublishReadinessResult {
  const blockers: string[] = [];
  const warnings: string[] = [];

  const spine = (book.chapters ?? []).filter((ch) => isSpineChapter(ch.kind));
  const withContent = spine.filter((ch) => chapterHasContent(ch.content));

  if (withContent.length === 0) {
    blockers.push("No chapters with content yet — write before you export.");
  }

  if (spine.length > 0) {
    const missingSummary = spine.filter((ch) => !String(ch.tabData?.summary ?? "").trim());
    if (missingSummary.length > spine.length / 2) {
      blockers.push(
        `More than half of chapters are missing summaries (${missingSummary.length}/${spine.length}).`
      );
    } else if (missingSummary.length > 0) {
      warnings.push(
        `${missingSummary.length} chapter${missingSummary.length === 1 ? "" : "s"} missing a planning summary.`
      );
    }
  }

  const storyCraft = opts?.storyCraftByChapter ?? {};
  for (const ch of withContent) {
    const entry = storyCraft[ch.id];
    const avg =
      typeof entry?.average === "number" && Number.isFinite(entry.average) ? entry.average : null;
    if (avg != null && avg < 3) {
      warnings.push(
        `"${ch.title || "Untitled"}" Story Craft average is ${avg.toFixed(1)} (< 3) — consider revising before inviting readers.`
      );
    }
  }

  const meta = (book.metadata ?? {}) as Record<string, unknown>;
  const betaFlag =
    meta.betaReadersInvited === true ||
    meta.betaReady === true ||
    meta.hasInvitedBetaReaders === true ||
    (typeof meta.betaReaderCount === "number" && meta.betaReaderCount > 0);

  if (!betaFlag) {
    warnings.push(
      'Promise of "invite readers": no beta-reader invite flag on this book yet — invite 3–5 readers before store upload.'
    );
  }

  return {
    ok: blockers.length === 0,
    blockers,
    warnings,
  };
}
